<template>
  <section class="slide-list-wrapper">
    <p
      v-if="!Object.keys(slides).length"
      class="slide-list__empty"
    >
      No slides available.
    </p>
    <div
      v-else
      class="slide-list"
      :style="gridStyle"
    >
      <button
        v-for="(image, id) in slides"
        :key="id"
        type="button"
        class="slide-card"
        :class="{ 
          'slide-card--active': id === currentSelectedSlide,
          'slide-card--deleted': image.is_deleted
        }"
        @click="handleSelect(id as string)"
      >
        <div
          v-if="image.image_url"
          class="slide-card__thumbnail"
        >
          <img
            :src="image.image_url"
            :alt="`Slide thumbnail ${id}`"
            loading="lazy"
          >
          <SlideActionButtons
            :is-deleted="!!image.is_deleted"
            :interactive="props.interactive"
            @delete="$emit('delete', id as string)"
            @restore="$emit('restore', id as string)"
          />
        </div>
        <span class="slide-card__meta">{{ filenameFromUrl(image.image_url ?? '') }}</span>
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type {
  AnnotationSchemaPayload,
} from '@/components/RegionBasedAnnotation/types/annotation';
import SlideActionButtons from './SlideActionButtons.vue';

function filenameFromUrl(url: string) {
  if (!url) return '';
  try {
    const parsed = url.startsWith('/') && typeof window !== 'undefined'
      ? new URL(url, window.location.origin)
      : new URL(url);
    const encodedPath = parsed.searchParams.get('path');
    if (encodedPath) {
      const decodedPath = decodeURIComponent(encodedPath);
      const pathSegments = decodedPath.split('/');
      return pathSegments[pathSegments.length - 1] || decodedPath;
    }
  } catch (error) {
    console.warn('Warning: Error parsing URL:', error);
  }
  const withoutQuery = url.split('?')[0];
  const segments = withoutQuery.split('/');
  return segments[segments.length - 1] || withoutQuery;
}

const props = withDefaults(defineProps<{
  columns?: number,
  images?: AnnotationSchemaPayload | null,
  currentSelectedSlide?: string | null,
  interactive?: boolean,
}>(), {
  columns: 1,
  images: null,
  currentSelectedSlide: null,
  interactive: true,
});

const emit = defineEmits<{
  (e: 'select', slideId: string): void,
  (e: 'delete', slideId: string): void,
  (e: 'restore', slideId: string): void,
}>();

const slides = computed(() => props.images ?? {});
const currentSelectedSlide = computed(() => props.currentSelectedSlide ?? null);

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${Math.max(1, props.columns)}, minmax(0, 1fr))`,
}));

function handleSelect(slideId: string) {
  const slide = slides.value[slideId];
  if (!slide || slide.is_deleted) return;
  if (slideId === currentSelectedSlide.value) return;
  emit('select', slideId);
  if (import.meta.env?.MODE === 'development') {
    console.log('[RBCSlideList] select', { slideId, currentSelectedSlide: currentSelectedSlide.value });
  }
}

</script>

<style scoped>
.slide-list-wrapper {
  width: 100%;
  height: 100%;
}

.slide-list__empty {
  margin: 0;
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
}

.slide-list {
  display: grid;
  gap: 12px;
}

.slide-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  text-align: left;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
}

.slide-card--active {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

.slide-card--deleted .slide-card__thumbnail img,
.slide-card--deleted .slide-card__meta {
  opacity: 0.5;
  filter: grayscale(1);
}

.slide-card__thumbnail {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-card__thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.slide-card__meta {
  display: block;
  color: #4b5563;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
